import { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  PersonOff as OfflineIcon,
} from '@mui/icons-material';
import { connectionsApi, offlinePlayersApi, squareRequestsApi } from '../../api';
import type { SearchConnectionsResult } from '../../api/connections';
import type { PlayerConnectionWithStats, OfflinePlayer, SearchOfflinePlayersResult } from '../../types';
import { PlayerAddRow } from './PlayerAddRow';
import { usePlayersStore } from '../../stores';

export interface AddConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  boardId: string;
  existingPlayerIds: string[]; // User IDs of existing online players
  existingOfflinePlayerIds: string[]; // IDs of existing offline players
  onPlayersChanged: () => void;
  squaresPerCell?: number;
}

interface AddedConnection {
  id: string;
  squares: number;
}

export const AddConnectionDialog = memo<AddConnectionDialogProps>(({
  open,
  onClose,
  boardId,
  existingPlayerIds,
  existingOfflinePlayerIds,
  onPlayersChanged,
  squaresPerCell = 1,
}) => {
  const unitLabel = squaresPerCell > 1 ? 'Cells' : 'Squares';
  const totalCells = squaresPerCell > 1 ? (squaresPerCell === 2 ? 50 : 25) : 100;

  // Use global store to refresh players list (pass boardId directly to ensure it's always set)
  const fetchOfflinePlayersToStore = usePlayersStore((state) => state.fetchOfflinePlayers);

  const [activeTab, setActiveTab] = useState(0); // 0 = Online, 1 = Offline
  const [hideAdded, setHideAdded] = useState(false); // Toggle to hide already-added players
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchResults, setSearchResults] = useState<SearchConnectionsResult | null>(null);
  const [offlineSearchResults, setOfflineSearchResults] = useState<SearchOfflinePlayersResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [addedConnections, setAddedConnections] = useState<AddedConnection[]>([]);
  const [squaresInput, setSquaresInput] = useState<Record<string, number>>({});
  const [startingBalanceInput, setStartingBalanceInput] = useState<Record<string, number>>({});
  // Fresh board offline player IDs fetched when dialog opens (to avoid stale prop data)
  const [freshBoardOfflinePlayerIds, setFreshBoardOfflinePlayerIds] = useState<string[]>([]);

  // Fetch fresh board offline players when dialog opens
  useEffect(() => {
    if (!open || !boardId) return;

    const fetchBoardOfflinePlayers = async () => {
      try {
        const players = await offlinePlayersApi.getBoardOfflinePlayers(boardId);
        setFreshBoardOfflinePlayerIds(players.map(p => p.id));
      } catch (err) {
        console.error('Failed to fetch board offline players:', err);
        // Fall back to prop data if fetch fails
        setFreshBoardOfflinePlayerIds(existingOfflinePlayerIds);
      }
    };

    fetchBoardOfflinePlayers();
  }, [open, boardId, existingOfflinePlayerIds]);

  // Debounced search for offline players
  useEffect(() => {
    if (!open || activeTab !== 1) return;

    const searchOfflinePlayers = async () => {
      setOfflineLoading(true);
      try {
        const result = await offlinePlayersApi.searchMyOfflinePlayers({
          query: searchQuery,
          limit: 20,
          offset: 0,
        });
        setOfflineSearchResults(result);
      } catch (err) {
        console.error('Failed to search offline players:', err);
        setError(err instanceof Error ? err.message : 'Failed to search offline players');
      } finally {
        setOfflineLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchOfflinePlayers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, open, activeTab]);

  // Debounced search for online connections
  useEffect(() => {
    if (!open || activeTab !== 0) return;

    const searchConnections = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await connectionsApi.search({
          query: searchQuery,
          limit: 20,
          offset: 0,
        });
        setSearchResults(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to search connections');
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchConnections, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, open, activeTab]);

  // Filter offline players based on hideAdded toggle (search is server-side)
  const filteredOfflinePlayers = useMemo(() => {
    if (!offlineSearchResults?.players) return [];
    if (!hideAdded) return offlineSearchResults.players;

    return offlineSearchResults.players.filter(p =>
      !freshBoardOfflinePlayerIds.includes(p.id) && !addedConnections.some(ac => ac.id === p.id)
    );
  }, [offlineSearchResults?.players, hideAdded, freshBoardOfflinePlayerIds, addedConnections]);

  // Filter connections based on hideAdded toggle
  const filteredConnections = useMemo(() => {
    if (!searchResults?.connections) return [];
    if (!hideAdded) return searchResults.connections;

    return searchResults.connections.filter(conn => {
      // Check if already on board
      if (conn.connectedUserId && existingPlayerIds.includes(conn.connectedUserId)) return false;
      if (conn.offlinePlayerId && freshBoardOfflinePlayerIds.includes(conn.offlinePlayerId)) return false;
      // Check if just added in this session
      if (addedConnections.some(ac => ac.id === conn.id)) return false;
      return true;
    });
  }, [searchResults?.connections, hideAdded, existingPlayerIds, freshBoardOfflinePlayerIds, addedConnections]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setSearchQuery('');
      setSearchResults(null);
      setOfflineSearchResults(null);
      setError(null);
      setAddedConnections([]);
      setSquaresInput({});
      setStartingBalanceInput({});
      setHideAdded(false);
      // Fresh data will be fetched by the other effect
    } else {
      // Clear fresh data when closing so it's refetched on next open
      setFreshBoardOfflinePlayerIds([]);
    }
  }, [open]);

  const isConnectionOnBoard = useCallback((conn: PlayerConnectionWithStats): boolean => {
    // Check if it's an online user connection
    if (conn.connectedUserId) {
      return existingPlayerIds.includes(conn.connectedUserId);
    }
    // Check if it's an offline player connection
    if (conn.offlinePlayerId) {
      return freshBoardOfflinePlayerIds.includes(conn.offlinePlayerId);
    }
    return false;
  }, [existingPlayerIds, freshBoardOfflinePlayerIds]);

  const isOfflinePlayerOnBoard = useCallback((playerId: string): boolean => {
    return freshBoardOfflinePlayerIds.includes(playerId);
  }, [freshBoardOfflinePlayerIds]);

  const isJustAdded = useCallback((id: string): boolean => {
    return addedConnections.some(ac => ac.id === id);
  }, [addedConnections]);

  const getConnectionName = (conn: PlayerConnectionWithStats): string => {
    if (conn.nickname) return conn.nickname;
    if (conn.connectedUser) return conn.connectedUser.displayName;
    if (conn.offlinePlayer) return conn.offlinePlayer.displayName;
    return 'Unknown';
  };

  const getConnectionHandle = (conn: PlayerConnectionWithStats): string | undefined => {
    if (conn.connectedUser?.handle) return conn.connectedUser.handle;
    if (conn.offlinePlayer?.handle) return conn.offlinePlayer.handle;
    return undefined;
  };

  const getConnectionIcon = (conn: PlayerConnectionWithStats): string | undefined => {
    if (conn.connectedUser?.iconName) return conn.connectedUser.iconName;
    if (conn.offlinePlayer?.iconName) return conn.offlinePlayer.iconName;
    return undefined;
  };

  // Check if connection is to an online user or offline player
  const isOnlineConnection = (conn: PlayerConnectionWithStats): boolean => {
    return !!conn.connectedUserId;
  };

  const getSquaresForId = (id: string): number => {
    return squaresInput[id] ?? 1;
  };

  const getStartingBalanceForId = (id: string): number => {
    return startingBalanceInput[id] ?? 0;
  };

  const handleAddConnection = async (conn: PlayerConnectionWithStats) => {
    const squares = getSquaresForId(conn.id);
    const startingBalance = getStartingBalanceForId(conn.id);
    setAddingId(conn.id);
    setError(null);

    try {
      if (conn.connectedUserId) {
        // For online users, create an auto-approved square request with optional starting balance
        await squareRequestsApi.assignSquares(boardId, {
          userId: conn.connectedUserId,
          squares: squares,
          startingBalance: startingBalance !== 0 ? startingBalance : undefined,
        });
      } else if (conn.offlinePlayerId) {
        // For offline player connections, add the offline player to the board
        await offlinePlayersApi.addToBoard(boardId, conn.offlinePlayerId, {
          approvedSquares: squares,
          startingBalance: startingBalance !== 0 ? startingBalance : undefined,
        });
      }

      setAddedConnections(prev => [...prev, { id: conn.id, squares }]);
      // Refresh global store (pass boardId directly) and notify parent
      fetchOfflinePlayersToStore(boardId);
      onPlayersChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add connection to board');
    } finally {
      setAddingId(null);
    }
  };

  const handleAddOfflinePlayer = async (player: OfflinePlayer) => {
    const squares = getSquaresForId(player.id);
    const startingBalance = getStartingBalanceForId(player.id);
    setAddingId(player.id);
    setError(null);

    try {
      await offlinePlayersApi.addToBoard(boardId, player.id, {
        approvedSquares: squares,
        startingBalance: startingBalance !== 0 ? startingBalance : undefined,
      });

      setAddedConnections(prev => [...prev, { id: player.id, squares }]);
      // Refresh global store (pass boardId directly) and notify parent
      fetchOfflinePlayersToStore(boardId);
      onPlayersChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add offline player to board');
    } finally {
      setAddingId(null);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchQuery(''); // Clear search when switching tabs
    // Focus search input after tab switch
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  const loadMore = async () => {
    if (!searchResults || loading) return;

    const nextOffset = searchResults.offset + searchResults.limit;
    if (nextOffset >= searchResults.total) return;

    setLoading(true);
    try {
      const result = await connectionsApi.search({
        query: searchQuery,
        limit: 20,
        offset: nextOffset,
      });
      setSearchResults({
        ...result,
        connections: [...searchResults.connections, ...result.connections],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more connections');
    } finally {
      setLoading(false);
    }
  };

  const hasMore = searchResults && searchResults.offset + searchResults.connections.length < searchResults.total;

  const loadMoreOffline = async () => {
    if (!offlineSearchResults || offlineLoading) return;

    const nextOffset = offlineSearchResults.offset + offlineSearchResults.limit;
    if (nextOffset >= offlineSearchResults.total) return;

    setOfflineLoading(true);
    try {
      const result = await offlinePlayersApi.searchMyOfflinePlayers({
        query: searchQuery,
        limit: 20,
        offset: nextOffset,
      });
      setOfflineSearchResults({
        ...result,
        players: [...(offlineSearchResults.players || []), ...(result.players || [])],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more offline players');
    } finally {
      setOfflineLoading(false);
    }
  };

  const hasMoreOffline = offlineSearchResults?.players && offlineSearchResults.offset + offlineSearchResults.players.length < offlineSearchResults.total;

  // Render connections list item (supports both online user and offline player connections)
  const renderConnectionItem = (conn: PlayerConnectionWithStats) => (
    <PlayerAddRow
      key={conn.id}
      id={conn.id}
      name={getConnectionName(conn)}
      handle={getConnectionHandle(conn)}
      iconName={getConnectionIcon(conn)}
      isOnline={isOnlineConnection(conn)}
      isOnBoard={isConnectionOnBoard(conn)}
      justAdded={isJustAdded(conn.id)}
      isAdding={addingId === conn.id}
      squaresValue={getSquaresForId(conn.id)}
      balanceValue={getStartingBalanceForId(conn.id)}
      onSquaresChange={(val) => setSquaresInput(prev => ({ ...prev, [conn.id]: val }))}
      onBalanceChange={(val) => setStartingBalanceInput(prev => ({ ...prev, [conn.id]: val }))}
      onAdd={() => handleAddConnection(conn)}
      unitLabel={unitLabel}
      totalCells={totalCells}
    />
  );

  // Render offline player list item
  const renderOfflinePlayerItem = (player: OfflinePlayer) => (
    <PlayerAddRow
      key={player.id}
      id={player.id}
      name={player.displayName}
      handle={player.handle}
      iconName={player.iconName}
      isOnline={false}
      isOnBoard={isOfflinePlayerOnBoard(player.id)}
      justAdded={isJustAdded(player.id)}
      isAdding={addingId === player.id}
      squaresValue={getSquaresForId(player.id)}
      balanceValue={getStartingBalanceForId(player.id)}
      onSquaresChange={(val) => setSquaresInput(prev => ({ ...prev, [player.id]: val }))}
      onBalanceChange={(val) => setStartingBalanceInput(prev => ({ ...prev, [player.id]: val }))}
      onAdd={() => handleAddOfflinePlayer(player)}
      unitLabel={unitLabel}
      totalCells={totalCells}
    />
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="add-players-dialog">
      <DialogTitle>Add Players to Board</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          aria-label="Player type tabs"
          data-testid="add-players-tabs"
        >
          <Tab
            id="tab-connections"
            aria-controls="tabpanel-connections"
            icon={<PersonIcon />}
            iconPosition="start"
            label={`Connections (${searchResults?.total ?? 0})`}
            data-testid="tab-connections"
          />
          <Tab
            id="tab-offline"
            aria-controls="tabpanel-offline"
            icon={<OfflineIcon />}
            iconPosition="start"
            label={`Offline Players (${offlineSearchResults?.total ?? 0})`}
            data-testid="tab-offline"
          />
        </Tabs>

        <TextField
          fullWidth
          placeholder={activeTab === 0 ? 'Search connections...' : 'Search offline players...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          inputRef={searchInputRef}
          autoFocus
          data-testid="add-players-search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: ((loading && activeTab === 0) || (offlineLoading && activeTab === 1)) && searchQuery ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 1 }}
        />

        {/* Hide Added Toggle */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideAdded}
              onChange={(e) => setHideAdded(e.target.checked)}
            />
          }
          label={<Typography variant="body2" color="text.secondary">Hide added players</Typography>}
          sx={{ mb: 1 }}
        />

        {/* Online Connections Tab */}
        {activeTab === 0 && (
          <Box
            role="tabpanel"
            id="tabpanel-connections"
            aria-labelledby="tab-connections"
            data-testid="tabpanel-connections"
          >
            {loading && !searchResults && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!loading && searchResults && filteredConnections.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary">
                  {hideAdded && searchResults.connections.length > 0
                    ? 'All connections already added'
                    : `No connections found${searchQuery ? ` matching "${searchQuery}"` : ''}`}
                </Typography>
              </Box>
            )}

            {filteredConnections.length > 0 && (
              <Box sx={{ maxHeight: 350, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredConnections.map(renderConnectionItem)}
                {hasMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={loadMore}
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Offline Players Tab */}
        {activeTab === 1 && (
          <Box
            role="tabpanel"
            id="tabpanel-offline"
            aria-labelledby="tab-offline"
            data-testid="tabpanel-offline"
          >
            {offlineLoading && !offlineSearchResults && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!offlineLoading && offlineSearchResults && filteredOfflinePlayers.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary">
                  {hideAdded && offlineSearchResults.players?.length > 0
                    ? 'All offline players already added'
                    : `No offline players found${searchQuery ? ` matching "${searchQuery}"` : ''}`}
                </Typography>
              </Box>
            )}

            {filteredOfflinePlayers.length > 0 && (
              <Box sx={{ maxHeight: 350, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredOfflinePlayers.map(renderOfflinePlayerItem)}
                {hasMoreOffline && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={loadMoreOffline}
                      disabled={offlineLoading}
                    >
                      {offlineLoading ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant={addedConnections.length > 0 ? 'contained' : 'text'}
          data-testid="add-players-close"
        >
          {addedConnections.length > 0 ? 'Done' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

AddConnectionDialog.displayName = 'AddConnectionDialog';
